import React, { useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import { makeStyles } from '@material-ui/core/styles'
import { IconButton, Typography } from '@material-ui/core'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import { useQuery } from 'react-query'
import { HoursOfOperation, DaySpec } from 'src/api/models'
import { skuit } from 'src/services/skuit'

const SLOT_MINUTES = 30
const SLOT_HEIGHT = 22
const TOTAL_SLOTS = (24 * 60) / SLOT_MINUTES // 48
const DEFAULT_DURATION_SLOTS = 2 // 1 hour
const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const

function getDayHours(
  date: moment.Moment,
  hoursOfOperation?: HoursOfOperation
): DaySpec | undefined {
  if (!hoursOfOperation) return undefined
  const key = DAY_KEYS[date.day()]
  const spec = hoursOfOperation[key] ?? hoursOfOperation.default
  if (!spec || spec.open === spec.close) return undefined
  return spec
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    overflow: 'hidden',
    background: '#fff',
    marginBottom: theme.spacing(2),
    userSelect: 'none',
  },
  calendarPanel: {
    width: 196,
    flexShrink: 0,
    borderRight: '1px solid #e0e0e0',
    padding: theme.spacing(1),
  },
  monthNav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 2,
  },
  monthTitle: {
    fontWeight: 600,
    fontSize: 12,
  },
  calendarGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    textAlign: 'center',
  },
  dowLabel: {
    fontSize: 10,
    color: theme.palette.text.secondary,
    padding: '2px 0',
    fontWeight: 600,
  },
  dayCell: {
    fontSize: 11,
    borderRadius: '50%',
    cursor: 'pointer',
    lineHeight: '24px',
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      background: theme.palette.action.hover,
    },
  },
  dayCellSelected: {
    background: '#1a73e8 !important',
    color: '#fff !important',
  },
  dayCellToday: {
    color: '#1a73e8',
    fontWeight: 700,
  },
  timePanel: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  timePanelHeader: {
    padding: '4px 8px',
    borderBottom: '1px solid #e0e0e0',
    background: '#fafafa',
    flexShrink: 0,
  },
  timeGridScroll: {
    overflowY: 'auto',
    maxHeight: 228,
  },
  slotRow: {
    display: 'flex',
    alignItems: 'stretch',
    height: SLOT_HEIGHT,
    cursor: 'pointer',
  },
  slotLabel: {
    width: 36,
    flexShrink: 0,
    textAlign: 'right',
    paddingRight: 6,
    fontSize: 10,
    color: theme.palette.text.secondary,
    lineHeight: `${SLOT_HEIGHT}px`,
    borderRight: '1px solid #f0f0f0',
  },
  slotBlock: {
    flex: 1,
    borderTop: '1px solid #f5f5f5',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  slotBlockClosed: {
    background: '#f5f5f5',
  },
  slotBlockSelected: {
    background: '#BBDEFB',
    borderTopColor: '#90CAF9',
  },
  slotBlockHover: {
    background: '#E8F0FE',
  },
  slotBlockSelectionStart: {
    borderTop: '2px solid #1a73e8 !important',
  },
  slotBlockSelectionEnd: {
    borderBottom: '2px solid #1a73e8',
  },
  slotCount: {
    fontSize: 9,
    fontWeight: 700,
    lineHeight: 1,
    paddingRight: 4,
    color: '#fff',
    background: '#78909C',
    borderRadius: 3,
    padding: '1px 3px',
    marginRight: 3,
    flexShrink: 0,
    pointerEvents: 'none',
  },
  slotCountBusy: {
    background: '#EF6C00',
  },
  slotCountFull: {
    background: '#C62828',
  },
}))

export interface MiniSchedulerProps {
  start?: moment.Moment
  end?: moment.Moment
  onChange: (start: moment.Moment, end: moment.Moment) => void
  hoursOfOperation?: HoursOfOperation
  facilityGuid?: string
  excludeGuid?: string // guid of the appointment being edited (exclude from counts)
}

export const MiniScheduler = ({
  start,
  end,
  onChange,
  hoursOfOperation,
  facilityGuid,
  excludeGuid,
}: MiniSchedulerProps) => {
  const classes = useStyles()
  const scrollRef = useRef<HTMLDivElement>(null)
  const isDragging = useRef(false)

  const [viewMonth, setViewMonth] = useState<moment.Moment>(() =>
    (start ?? moment()).clone().startOf('month')
  )
  const [selectedDate, setSelectedDate] = useState<moment.Moment>(() =>
    (start ?? moment()).clone().startOf('day')
  )
  const [dragStartSlot, setDragStartSlot] = useState<number | null>(null)
  const [hoverSlot, setHoverSlot] = useState<number | null>(null)

  // Sync when start date changes externally (e.g., user types in text field)
  const startDateKey = start?.format('YYYY-MM-DD')
  useEffect(() => {
    if (start) {
      setSelectedDate(start.clone().startOf('day'))
      setViewMonth(start.clone().startOf('month'))
    }
  }, [startDateKey]) // eslint-disable-line

  const selectedDateKey = selectedDate.format('YYYY-MM-DD')

  const dayHours = getDayHours(selectedDate, hoursOfOperation)
  const openSlot = dayHours ? Math.floor(dayHours.open / SLOT_MINUTES) : 0
  const closeSlot = dayHours ? Math.ceil(dayHours.close / SLOT_MINUTES) : TOTAL_SLOTS
  // Always render all slots; grey out those outside operating hours
  const firstSlot = 0
  const lastSlot = TOTAL_SLOTS
  const isSlotClosed = (slot: number): boolean => {
    if (!dayHours) return false
    return slot < openSlot || slot >= closeSlot
  }

  // Scroll to show start time (or beginning of operating hours) when date changes
  useEffect(() => {
    const scrollEl = scrollRef.current
    if (!scrollEl) return
    let targetSlot: number
    if (start && start.isSame(selectedDate, 'day')) {
      const mins = start.diff(start.clone().startOf('day'), 'minutes')
      targetSlot = Math.floor(mins / SLOT_MINUTES)
    } else {
      targetSlot = firstSlot
    }
    scrollEl.scrollTop = Math.max(0, targetSlot - 2) * SLOT_HEIGHT
  }, [selectedDateKey]) // eslint-disable-line

  // Fetch existing appointments for the selected day
  const appointmentsQuery = useQuery(
    ['mini-scheduler-appointments', facilityGuid, selectedDateKey],
    () =>
      skuit.sc.appointments.list({
        params: {
          page_size: 200,
          facilities: facilityGuid,
          start_after: selectedDate.clone().startOf('day').toISOString(),
          start_before: selectedDate.clone().endOf('day').toISOString(),
        },
      }),
    { enabled: !!facilityGuid }
  )

  // Compute appointment count per slot
  const slotCounts = useMemo(() => {
    const counts = new Array<number>(TOTAL_SLOTS).fill(0)
    const results = appointmentsQuery.data?.results
    if (!results) return counts
    for (const appt of results) {
      if (!appt.start || !appt.end) continue
      if (excludeGuid && appt.guid === excludeGuid) continue
      const apptStart = appt.start as moment.Moment
      const apptEnd = appt.end as moment.Moment
      if (!apptStart.isSame(selectedDate, 'day')) continue
      const dayStart = apptStart.clone().startOf('day')
      const fromSlot = Math.floor(apptStart.diff(dayStart, 'minutes') / SLOT_MINUTES)
      const toSlot = Math.ceil(apptEnd.diff(dayStart, 'minutes') / SLOT_MINUTES)
      for (let s = fromSlot; s < toSlot && s < TOTAL_SLOTS; s++) {
        if (s >= 0) counts[s]++
      }
    }
    return counts
  }, [appointmentsQuery.data?.results, selectedDateKey, excludeGuid]) // eslint-disable-line

  const slotToMoment = (slot: number) => selectedDate.clone().add(slot * SLOT_MINUTES, 'minutes')

  const momentToSlot = (m: moment.Moment): number => {
    const mins = m.diff(m.clone().startOf('day'), 'minutes')
    return Math.floor(mins / SLOT_MINUTES)
  }

  const startSlot = start && start.isSame(selectedDate, 'day') ? momentToSlot(start) : null
  const endSlot = end && end.isSame(selectedDate, 'day') ? momentToSlot(end) - 1 : null // inclusive

  const previewStart =
    dragStartSlot !== null && hoverSlot !== null ? Math.min(dragStartSlot, hoverSlot) : null
  const previewEnd =
    dragStartSlot !== null && hoverSlot !== null ? Math.max(dragStartSlot, hoverSlot) : null

  const isSlotSelected = (slot: number): boolean => {
    if (previewStart !== null && previewEnd !== null) {
      return slot >= previewStart && slot <= previewEnd
    }
    if (startSlot !== null && endSlot !== null) {
      return slot >= startSlot && slot <= endSlot
    }
    return false
  }

  const finalizeSelection = (endSlotIdx: number) => {
    if (!isDragging.current || dragStartSlot === null) return
    isDragging.current = false
    const fromSlot = Math.min(dragStartSlot, endSlotIdx)
    const toSlot = Math.max(dragStartSlot, endSlotIdx)
    // Single-slot click → default to 1 hour; drag selects exact range + one slot
    const endOffset = fromSlot === toSlot ? DEFAULT_DURATION_SLOTS : toSlot - fromSlot + 1
    onChange(slotToMoment(fromSlot), slotToMoment(fromSlot + endOffset))
    setDragStartSlot(null)
  }

  const handleSlotMouseDown = (slot: number) => {
    isDragging.current = true
    setDragStartSlot(slot)
    setHoverSlot(slot)
  }

  const handleSlotMouseEnter = (slot: number) => {
    setHoverSlot(slot)
  }

  const handleSlotMouseUp = (slot: number) => {
    finalizeSelection(slot)
  }

  const handleRootMouseUp = () => {
    if (isDragging.current && hoverSlot !== null) {
      finalizeSelection(hoverSlot)
    } else {
      isDragging.current = false
      setDragStartSlot(null)
    }
  }

  // Mini calendar grid
  const today = moment().startOf('day')
  const firstDayOffset = viewMonth.clone().startOf('month').day()
  const daysInMonth = viewMonth.daysInMonth()
  const calendarCells: (moment.Moment | null)[] = [
    ...Array(firstDayOffset).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => viewMonth.clone().date(i + 1)),
  ]

  const handleDayClick = (day: moment.Moment) => {
    const newDate = day.clone()
    setSelectedDate(newDate)
    if (start) {
      const newStart = newDate.clone().hours(start.hours()).minutes(start.minutes()).seconds(0)
      const duration = end ? end.diff(start, 'minutes') : 60
      const newEnd = newStart.clone().add(duration, 'minutes')
      onChange(newStart, newEnd)
    }
  }

  const slots = Array.from({ length: lastSlot - firstSlot }, (_, i) => i + firstSlot)

  const hoursLabel = dayHours
    ? `${slotToMoment(openSlot).format('h:mm A')} – ${slotToMoment(closeSlot).format('h:mm A')}`
    : null

  return (
    <div className={classes.root} onMouseUp={handleRootMouseUp} onMouseLeave={handleRootMouseUp}>
      {/* Mini month calendar */}
      <div className={classes.calendarPanel}>
        <div className={classes.monthNav}>
          <IconButton
            size="small"
            onClick={() => setViewMonth((m) => m.clone().subtract(1, 'month'))}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography className={classes.monthTitle}>{viewMonth.format('MMM YYYY')}</Typography>
          <IconButton size="small" onClick={() => setViewMonth((m) => m.clone().add(1, 'month'))}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </div>

        <div className={classes.calendarGrid}>
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
            <div key={d} className={classes.dowLabel}>
              {d}
            </div>
          ))}
          {calendarCells.map((day, i) => {
            if (!day) return <div key={`e${i}`} />
            const isSelected = day.isSame(selectedDate, 'day')
            const isToday = day.isSame(today, 'day')
            const cellClass = [
              classes.dayCell,
              isSelected ? classes.dayCellSelected : '',
              !isSelected && isToday ? classes.dayCellToday : '',
            ]
              .filter(Boolean)
              .join(' ')
            return (
              <div key={i} className={cellClass} onClick={() => handleDayClick(day)}>
                {day.date()}
              </div>
            )
          })}
        </div>
      </div>

      {/* Day time grid */}
      <div className={classes.timePanel}>
        <div className={classes.timePanelHeader}>
          <Typography variant="caption" style={{ fontWeight: 600 }}>
            {selectedDate.format('ddd, MMM D')}
          </Typography>
          {hoursLabel && (
            <Typography variant="caption" style={{ marginLeft: 6, color: '#777' }}>
              {hoursLabel}
            </Typography>
          )}
        </div>
        <div className={classes.timeGridScroll} ref={scrollRef}>
          {slots.map((slot) => {
            const isHourBoundary = slot % 2 === 0
            const closed = isSlotClosed(slot)
            const selected = isSlotSelected(slot)
            const isHover = dragStartSlot === null && hoverSlot === slot && !closed
            const isSelStart = dragStartSlot === null ? slot === startSlot : slot === previewStart
            const isSelEnd = dragStartSlot === null ? slot === endSlot : slot === previewEnd
            const count = slotCounts[slot]

            const blockClass = [
              classes.slotBlock,
              closed ? classes.slotBlockClosed : '',
              !closed && selected ? classes.slotBlockSelected : '',
              !closed && isHover ? classes.slotBlockHover : '',
              isSelStart ? classes.slotBlockSelectionStart : '',
              isSelEnd ? classes.slotBlockSelectionEnd : '',
            ]
              .filter(Boolean)
              .join(' ')

            const countClass = [
              classes.slotCount,
              count >= 3 ? classes.slotCountFull : '',
              count === 2 ? classes.slotCountBusy : '',
            ]
              .filter(Boolean)
              .join(' ')

            return (
              <div
                key={slot}
                className={classes.slotRow}
                onMouseDown={() => handleSlotMouseDown(slot)}
                onMouseEnter={() => handleSlotMouseEnter(slot)}
                onMouseUp={() => handleSlotMouseUp(slot)}
                onMouseLeave={() => {
                  if (!isDragging.current) setHoverSlot(null)
                }}
              >
                <div className={classes.slotLabel}>
                  {isHourBoundary ? slotToMoment(slot).format('H:mm') : ''}
                </div>
                <div className={blockClass}>
                  {count > 0 && <span className={countClass}>{count}</span>}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
